import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PyramidalShareholderSearchFilter } from '../models/pyramidalShareholderSearchFilter';
import { PaginatedResponse } from '../models/paginatedResponse';
import { BulkMergeRequest } from '../models/bulkMergeRequest';
import { PyramidalShareholderDto } from '../models/api/pyramidalShareholderDto';
import { PyramidalShareholderDetailDto } from '../models/api/pyramidalShareholderDetailDto';
import { PyramidalShareholderPyramidDto } from '../models/api/pyramidalShareholderPyramidDto';
import { CreatePyramidalShareholderPyramidRequest } from '../models/api/createPyramidalShareholderPyramidRequest';
import { DeletePyramidalShareholderPyramidRequest } from '../models/api/deletePyramidalShareholderPyramidRequest';
import { DeletePyramidalShareholderPyramidYearsRequest } from '../models/api/deletePyramidalShareholderPyramidYearsRequest';
import { UpdatePyramidalShareholderPyramidRequest } from '../models/api/updatePyramidalShareholderPyramidRequest';
import { SearchPyramidalShareholderEntitiesRequest } from '../models/api/searchPyramidalShareholderEntitiesRequest';
import { PyramidalShareholderEntityDto } from '../models/api/pyramidalShareholderEntityDto';
import { CreatePyramidalShareholderRequest } from '../models/api/createPyramidalShareholderRequest';
import { UpdatePyramidalShareholderRequest } from '../models/api/updatePyramidalShareholderRequest';
import { UpsertMultiYearPyramidalShareholderPyramidRequest } from '../models/api/upsertMultiYearPyramidalShareholderPyramidRequest';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PyramidalShareholderService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/pyramidal-shareholders`;

  /**
   * Search for pyramidal shareholders with pagination and filtering
   */
  searchPyramidalShareholders(request: PyramidalShareholderSearchFilter): Observable<PaginatedResponse<PyramidalShareholderDto>> {
    return this.http.post<PaginatedResponse<PyramidalShareholderDto>>(`${this.apiUrl}/search`, request);
  }

  /**
   * Export pyramidal shareholders to CSV based on search criteria
   */
  exportPyramidalShareholdersToCsv(request: Partial<PyramidalShareholderSearchFilter>): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/search/export`, request, {
      responseType: 'blob'
    });
  }

  /**
   * Get a pyramidal shareholder by ID with all pyramid entries grouped by year
   */
  getPyramidalShareholderById(id: string): Observable<PyramidalShareholderDetailDto> {
    return this.http.get<PyramidalShareholderDetailDto>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new pyramidal shareholder
   */
  createPyramidalShareholder(request: CreatePyramidalShareholderRequest): Observable<PyramidalShareholderDetailDto> {
    return this.http.post<PyramidalShareholderDetailDto>(this.apiUrl, request);
  }

  /**
   * Update an existing pyramidal shareholder
   */
  updatePyramidalShareholder(id: string, request: UpdatePyramidalShareholderRequest): Observable<PyramidalShareholderDetailDto> {
    return this.http.put<PyramidalShareholderDetailDto>(`${this.apiUrl}/${id}`, request);
  }

  /**
   * Delete a pyramidal shareholder
   */
  deletePyramidalShareholder(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Bulk merge multiple pyramidal shareholders into one
   */
  bulkMergePyramidalShareholders(request: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/merge/bulk`, request);
  }

  /**
   * Create a new pyramidal shareholder pyramid entry
   */
  createPyramid(request: CreatePyramidalShareholderPyramidRequest): Observable<PyramidalShareholderPyramidDto> {
    return this.http.post<PyramidalShareholderPyramidDto>(`${this.apiUrl}/pyramids`, request);
  }

  /**
   * Upsert pyramidal shareholder pyramid entries for multiple years
   * Creates or updates pyramid entries for the specified years
   */
  upsertMultiYearPyramid(request: UpsertMultiYearPyramidalShareholderPyramidRequest): Observable<PyramidalShareholderPyramidDto[]> {
    return this.http.post<PyramidalShareholderPyramidDto[]>(`${this.apiUrl}/pyramid`, request);
  }

  /**
   * Delete a pyramidal shareholder pyramid entry for a specific validity period
   */
  deletePyramid(request: DeletePyramidalShareholderPyramidRequest): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/pyramid`, { body: request });
  }

  /**
   * Delete a single pyramid node from a pyramidal shareholder's pyramid across several years, atomically.
   * The node is identified by its entity ids (not a row id) so the server can re-resolve the correct
   * historical row per year. Replaces the previous parallel one-request-per-year deletion.
   */
  deletePyramidYears(request: DeletePyramidalShareholderPyramidYearsRequest): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/pyramid/years`, { body: request });
  }

  /**
   * Update a pyramidal shareholder pyramid entry for a specific validity period
   */
  updatePyramid(id: string, request: UpdatePyramidalShareholderPyramidRequest): Observable<PyramidalShareholderPyramidDto> {
    return this.http.put<PyramidalShareholderPyramidDto>(`${this.apiUrl}/pyramids/${id}`, request);
  }

  /**
   * Search for entities by name that can be used in a pyramid entry.
   * Searches across PyramidalShareholders, LargeShareholders, Companies, OpbCompanies, and Directors.
   */
  searchPyramidEntities(request: SearchPyramidalShareholderEntitiesRequest): Observable<PyramidalShareholderEntityDto[]> {
    return this.http.post<PyramidalShareholderEntityDto[]>(`${this.apiUrl}/pyramids/search-entities`, request);
  }
}
